// src/client/components/settings/CategoriesTab.tsx
import React, { useState } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@client/components/ui/card';
import { Button } from '@client/components/ui/button';
import { Badge } from '@client/components/ui/badge';
import { Palette, Plus, RefreshCw } from 'lucide-react';
import { useCrudMutations } from '@client/hooks/useCrudMutations';
import { CrudDialog } from '@client/components/shared/CrudDialog';
import { FormField } from '@client/components/shared/FormField';
import { DataTable, TableColumn } from '@client/components/shared/DataTable';
import type { ProfileCategory, InsertProfileCategory } from '@client/types/settings';

interface CategoriesTabProps {
  categories: ProfileCategory[];
  loadCategories: () => Promise<void>;
  isLoading: boolean;
}

interface CategoryFormData {
  name: string;
  description: string;
  color: string;
  order: number;
}

export default function CategoriesTab({ 
  categories, 
  loadCategories, 
  isLoading 
}: CategoriesTabProps) {
  const [dialogMode, setDialogMode] = useState<'create' | 'edit' | null>(null);
  const [editingCategory, setEditingCategory] = useState<ProfileCategory | null>(null);
  const [formData, setFormData] = useState<CategoryFormData>({
    name: '',
    description: '',
    color: '#3B82F6',
    order: 0,
  });
  const [formError, setFormError] = useState<string | null>(null);

  // CRUD Mutations
  const { createMutation, updateMutation, deleteMutation } = useCrudMutations<ProfileCategory>({
    endpoint: '/profile-categories',
    queryKey: ['/profile-categories'],
    entityName: 'Category',
  });

  // Dialog Handlers
  const openCreateDialog = () => {
    setFormData({
      name: '',
      description: '',
      color: '#3B82F6',
      order: categories.length,
    });
    setFormError(null);
    setEditingCategory(null);
    setDialogMode('create');
  };

  const openEditDialog = (category: ProfileCategory) => {
    setEditingCategory(category);
    setFormData({
      name: category.name,
      description: category.description || '',
      color: category.color || '#3B82F6',
      order: category.order || 0,
    });
    setFormError(null);
    setDialogMode('edit');
  };

  const closeDialog = () => {
    setDialogMode(null);
    setEditingCategory(null);
    setFormData({ name: '', description: '', color: '#3B82F6', order: 0 });
    setFormError(null);
  };

  const handleSave = async () => {
    if (!formData.name.trim()) {
      setFormError('Category name ist erforderlich');
      return;
    }

    const categoryData: InsertProfileCategory = {
      name: formData.name.trim(),
      description: formData.description?.trim() || null,
      color: formData.color,
      order: formData.order || categories.length,
    };

    if (dialogMode === 'edit' && editingCategory) {
      updateMutation.mutate(
        { id: editingCategory.id, data: categoryData },
        {
          onSuccess: () => {
            closeDialog();
            loadCategories();
          },
        }
      );
    } else {
      createMutation.mutate(categoryData, {
        onSuccess: () => {
          closeDialog();
          loadCategories();
        },
      });
    }
  };

  const handleDelete = async (category: ProfileCategory) => {
    deleteMutation.mutate(category.id, {
      onSuccess: () => loadCategories(),
    });
  };

  // Table Columns
  const columns: TableColumn<ProfileCategory>[] = [
    {
      key: 'name',
      header: 'Name',
      render: (category) => (
        <div className="flex items-center gap-2">
          <div
            className="w-4 h-4 rounded"
            style={{ backgroundColor: category.color || '#3B82F6' }}
          />
          <span className="font-medium">{category.name}</span>
        </div>
      ),
    },
    {
      key: 'description',
      header: 'Description',
      render: (category) => category.description || '-',
    },
    {
      key: 'color',
      header: 'Color',
      render: (category) => (
        <Badge style={{ backgroundColor: category.color || '#3B82F6', color: 'white' }}>
          {category.color || '#3B82F6'}
        </Badge>
      ),
    },
    {
      key: 'order',
      header: 'Order',
      render: (category) => category.order || 0,
    },
    {
      key: 'createdAt',
      header: 'Created',
      render: (category) =>
        category.createdAt ? new Date(category.createdAt).toLocaleDateString() : '-',
    },
  ];

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Palette className="h-4 w-4" />
            <CardTitle>Profile Categories</CardTitle>
            <Button variant="ghost" size="sm" onClick={loadCategories} disabled={isLoading}>
              <RefreshCw className={`h-4 w-4 ${isLoading ? 'animate-spin' : ''}`} />
            </Button>
          </div>
          <Button onClick={openCreateDialog}>
            <Plus className="h-4 w-4 mr-2" />
            Add Category
          </Button>
        </div>
        <CardDescription>
          Manage profile categories stored in the database. Categories are used to organize profiles.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <DataTable
          data={categories}
          columns={columns}
          loading={isLoading}
          getRowKey={(category) => category.id}
          emptyState={{
            title: 'Keine Kategorien gefunden',
            description: 'Erstellen Sie Ihre erste Kategorie oben.',
          }}
          actions={{
            onEdit: openEditDialog,
            onDelete: handleDelete,
          }}
          deleteConfirmation={{
            title: 'Category löschen?',
            description: (category) =>
              `Sind Sie sicher, dass Sie die Kategorie "${category.name}" löschen möchten?`,
          }}
        />

        {/* Category Statistics */}
        {categories.length > 0 && (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 pt-4 border-t">
            <div className="text-center">
              <div className="text-lg font-semibold">{categories.length}</div>
              <div className="text-sm text-muted-foreground">Total Categories</div>
            </div>
            <div className="text-center">
              <div className="text-lg font-semibold">
                {categories.filter((c) => c.description).length}
              </div>
              <div className="text-sm text-muted-foreground">With Description</div>
            </div>
            <div className="text-center">
              <div className="text-lg font-semibold">
                {new Set(categories.map((c) => c.color)).size}
              </div>
              <div className="text-sm text-muted-foreground">Unique Colors</div>
            </div>
            <div className="text-center">
              <div className="text-lg font-semibold">
                {
                  categories.filter(
                    (c) =>
                      c.createdAt &&
                      new Date(c.createdAt).toDateString() === new Date().toDateString()
                  ).length
                }
              </div>
              <div className="text-sm text-muted-foreground">Created Today</div>
            </div>
          </div>
        )}
      </CardContent>

      {/* CRUD Dialog */}
      <CrudDialog
        open={dialogMode !== null}
        onOpenChange={(open) => !open && closeDialog()}
        mode={dialogMode || 'create'}
        title={dialogMode === 'edit' ? 'Category bearbeiten' : 'Neue Category'}
        description={
          dialogMode === 'edit'
            ? `Ändern Sie die Eigenschaften der Kategorie "${editingCategory?.name}".`
            : 'Erstellen Sie eine neue Profil-Kategorie.'
        }
        onSave={handleSave}
        onCancel={closeDialog}
        isSaving={createMutation.isPending || updateMutation.isPending}
      >
        <FormField
          id="name"
          label="Name"
          value={formData.name}
          onChange={(value) => setFormData({ ...formData, name: String(value) })}
          placeholder="z.B. VIP, Prospect"
          required
        />

        <FormField
          id="description"
          label="Description"
          type="textarea"
          value={formData.description}
          onChange={(value) => setFormData({ ...formData, description: String(value) })}
          placeholder="Beschreibung der Kategorie..."
          rows={3}
        />

        <div className="grid grid-cols-2 gap-4">
          <FormField
            id="color"
            label="Color"
            type="color"
            value={formData.color}
            onChange={(value) => setFormData({ ...formData, color: String(value) })}
          />

          <FormField
            id="order"
            label="Order"
            type="number"
            value={formData.order}
            onChange={(value) => setFormData({ ...formData, order: Number(value) || 0 })}
          />
        </div>

        {formError && <p className="text-sm text-red-500">{formError}</p>}
      </CrudDialog>
    </Card>
  );
}